// ─────────────────────────────────────────────────────────────────────────────
// Trade Entry guide page
//
// Walks the user through the 3-stage logging wizard:
//   1. Pre-Trade → 2. Execution → 3. Reflection
// Preceded by a Step 0 account-selection prep step, and with a CSV import
// shortcut available at the Pre-Trade stage (opens a file picker; selected
// file imports directly).
//
// Emphasis on:
//   - which fields are required vs optional
//   - which fields unlock which detectors (the engagement loop)
//   - the rule-violation warning at reflection time
//   - the demo-mode banner for non-subscribed users
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, H3, P, UL, LI, Field, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideTradeEntry() {
  return (
    <GuideLayout
      title="Trade Entry"
      subtitle="Log closed trades through a 3-stage wizard, or import in bulk from your broker's CSV export."
      prev={{ slug: "dashboard",   label: "Dashboard" }}
      next={{ slug: "can-i-trade", label: "Can I Trade?" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Trade Entry is where every closed trade gets logged. It's
        structured as a 3-stage wizard rather than a single long form
        because the trade's story has three different chapters: what you
        thought going in (Pre-Trade), what actually happened (Execution),
        and how you felt about it after (Reflection). Each stage captures
        one chapter while the details are still fresh.
      </P>
      <P>
        Before the wizard starts, you'll pick which account the trade
        belongs to. After that, the 3 stages walk in order.
      </P>
      <P>
        You can also bulk-import from your broker's CSV export instead of
        logging trades one at a time — useful for getting your trading
        history into the system on day one.
      </P>

      <H2>Two ways in</H2>
      <P>
        Click <UI>Trade Entry</UI> in the sidebar (or the <UI>Log Trade</UI>{" "}
        action card on the Dashboard) and you'll see the 3-stage wizard.
        Manual entry walks you through Pre-Trade, Execution, and
        Reflection one stage at a time.
      </P>
      <P>
        At the Pre-Trade stage there's also an{" "}
        <UI>📋 Import CSV (TopStep, Tradovate, etc.)</UI> button for
        bulk-importing your broker's export. Clicking it opens your
        computer's file picker; once you select a file, an{" "}
        <UI>Import Preview</UI> modal opens before anything is saved:
      </P>
      <UL>
        <LI><UI>NEW</UI> count — how many trades from the file aren't already in your journal</LI>
        <LI><UI>ALREADY IMPORTED</UI> count — duplicates Futures OS detected and will skip</LI>
        <LI>A preview of the first 5 trades, so you can confirm the parser read your file correctly before committing</LI>
        <LI><UI>Cancel</UI> or <UI>Import N new trades</UI> buttons</LI>
      </UL>

      <ScreenshotFrame
        src="/guide/trade-entry-import-preview.png"
        alt="CSV Import Preview modal"
        caption="The preview shows what will be imported, what's already in your journal, and a sample of trades — nothing saves until you confirm."
      />

      <Callout kind="tip" title="Re-importing the same file is safe">
        Futures OS deduplicates on import. If you re-import a CSV that
        already has trades in your journal, those rows show up under
        ALREADY IMPORTED and are skipped — only genuinely new trades
        get added. Useful if you accidentally import twice, or if you
        export the same range from your broker again later with newer
        trades appended.
      </Callout>

      <P>
        Manual entry captures the behavioral fields that broker exports
        don't include (mental state, confidence, exit reason). CSV import
        captures the trade mechanics fast. Most users do both: import
        their history first, then log new trades manually as they happen.
      </P>

      <Callout kind="warn" title="Demo mode">
        If you're using a demo account or your trial hasn't activated,
        you'll see a "Trade logging is disabled in the demo" banner at
        the top of the wizard. The form still walks but won't save. Sign
        up for the 30-day trial to enable logging.
      </Callout>

      <H2>Step 0 — Select Trading Account</H2>
      <P>
        Before the wizard begins, pick which account this trade belongs
        to. The dropdown lists every account you've added in{" "}
        <UI>Portfolio</UI>, prefixed with the prop firm name so you can
        tell accounts apart at a glance (e.g. "Apex — My $50K Account").
      </P>
      <P>
        Once you select an account, a context panel appears below the
        dropdown showing the prop firm, account type, balance, payout
        split, and platform — a quick reminder of the account you're
        about to trade on before you proceed.
      </P>
      <P>
        This matters because each account in Futures OS keeps its own
        trade history, its own drawdown tracking, and its own rules. A
        trade on your TopStep combine doesn't show up against your Apex
        funded account — they're separate ledgers.
      </P>
      <P>
        All trades you log in this session stay attached to the account
        you pick here, until you change it. Click{" "}
        <UI>Continue to Pre-Trade →</UI> when you're ready to start
        logging.
      </P>

      <H2>Stage 1 — Pre-Trade</H2>
      <P>
        What you saw, what you decided, what you were feeling going in.
        This is the chapter most trade journals skip — and the chapter
        most detectors depend on.
      </P>

      <ScreenshotFrame
        src="/guide/trade-entry-pretrade.png"
        alt="Pre-Trade stage of the Trade Entry wizard"
        caption="Pre-Trade captures market read, strategy choice, and conviction before execution details."
      />

      <H3>Fields on this stage</H3>
      <UL>
        <LI>
          <UI>Market Condition</UI> — pick one: <Field>TRENDING UP</Field>,{" "}
          <Field>TRENDING DOWN</Field>, <Field>RANGING</Field>, or{" "}
          <Field>VOLATILE</Field>. This unlocks Market Regime Edge in the
          Context analytics tab (which conditions you actually trade well
          in).
        </LI>
        <LI>
          <UI>Entry Signal / Strategy</UI> — what setup you took.
          Built-in choices include Pullback, Breakout, VWAP Bounce, EMA
          Ribbon, and others; you can add your own in <UI>Strategies</UI>.
          This unlocks Setup Edge Ranking (which of your setups make
          money).
        </LI>
        <LI>
          <UI>Conviction Level</UI> — 1–10 slider for how strongly you
          believed in the setup before entering. Unlocks Conviction
          Calibration (does your gut feeling correlate with actual
          outcomes?).
        </LI>
        <LI>
          <UI>Trade during or around a significant news event</UI> —
          checkbox. Unlocks News Event Trading in Context analytics.
        </LI>
      </UL>

      <Callout kind="tip">
        Conviction Level is one of the most revealing fields in the whole
        system. Most traders assume their high-conviction trades are
        their best ones. Logging this field over time often reveals the
        opposite — and that's a finding you can actually trade against.
      </Callout>

      <H2>Stage 2 — Execution</H2>
      <P>
        The mechanics — symbol, prices, size, time, direction.
      </P>

      <ScreenshotFrame
        src="/guide/trade-entry-execution.png"
        alt="Execution stage of the Trade Entry wizard"
        caption="Execution captures the trade mechanics: symbol, direction, prices, size, fees, and timing."
      />

      <H3>Required fields</H3>
      <UL>
        <LI><UI>Trade Direction</UI> — Long or Short</LI>
        <LI><UI>Entry Price</UI> and <UI>Exit Price</UI></LI>
        <LI><UI>Entry Time</UI> and <UI>Exit Time</UI> — the timestamps when you entered and exited</LI>
        <LI><UI>Position Size</UI> — the number of contracts</LI>
      </UL>

      <H3>Strongly recommended</H3>
      <UL>
        <LI>
          <UI>Stop Loss</UI> — without this, the Risk discipline detectors
          (Stop Adherence, Stop Widening, R-Multiple Distribution) can't
          run on this trade
        </LI>
        <LI>
          <UI>Take Profit</UI> — combined with Stop Loss, populates the{" "}
          <UI>Risk : Reward</UI> readout below the fields
        </LI>
        <LI><UI>Commissions</UI> and <UI>Fees</UI> — for accurate Net
          P&L. Without them, Gross and Net are identical.</LI>
      </UL>

      <Callout kind="info">
        Required fields have a <strong>*</strong> next to the label.
        Everything else is optional — you can save a trade with just
        prices and direction and come back to fill in details later from
        the Journal. But the more you fill in here, the less editing
        you'll do later.
      </Callout>

      <H2>Stage 3 — Reflection</H2>
      <P>
        The post-trade chapter: how the trade played out, how you felt,
        what you learned. This stage is also where Futures OS surfaces
        any rules you broke during the trade.
      </P>

      <ScreenshotFrame
        src="/guide/trade-entry-reflection.png"
        alt="Post-Trade Reflection stage with rule violation warning"
        caption="Reflection surfaces broken rules at the moment you're thinking back on the trade — not blocking, just noting."
      />

      <H3>Rule violation warning</H3>
      <P>
        If the trade broke any rules you've configured in{" "}
        <UI>Trading Rules</UI> for this account — outside your trade
        window, exceeded daily trade cap, news blackout, etc. — you'll
        see a yellow banner at the top of the Reflection stage listing
        what was violated.
      </P>
      <P>
        The trade still saves either way. The warning is a reflection
        prompt, not a block. The point isn't to prevent the trade after
        the fact — it's to make sure you see the pattern when it's
        already happened, so the next decision is informed.
      </P>

      <H3>Self-rating sliders (1–10)</H3>
      <UL>
        <LI><UI>Pre-Trade Confidence</UI> — how confident were you going in (separate from conviction in setup)</LI>
        <LI><UI>Focus Level</UI> — how focused was your session</LI>
        <LI><UI>Rule Adherence</UI> — how well did you follow your own rules during this trade</LI>
        <LI><UI>Impulsiveness</UI> — how reactive vs deliberate were you</LI>
        <LI><UI>Energy Level</UI> — your physical/mental energy at the time</LI>
        <LI><UI>Market Context Awareness</UI> — how clear was your read on broader conditions</LI>
        <LI><UI>Trade Outcome Satisfaction</UI> — separate from P&L — were you satisfied with the execution regardless of result</LI>
        <LI><UI>Fear Level</UI> and <UI>Greed Level</UI> — used by Fear-Spike Trading and emotion-driven win rate detectors</LI>
      </UL>

      <Callout kind="tip">
        These sliders feed the Psychology analytics suite. Patterns like
        "your win rate drops 100pp when fear is high" only appear once
        you've logged enough trades with these fields populated. They're
        worth the 20 seconds.
      </Callout>

      <H3>Dropdowns</H3>
      <UL>
        <LI>
          <UI>Pre-Trade Emotional State</UI> — Calm & Focused, Anxious /
          Hesitant, Excited / Eager, Frustrated / Tilted, Overconfident,
          Tired / Low Energy, FOMO
        </LI>
        <LI>
          <UI>Exit Reason</UI> — Target Hit, Stop Hit, Manual Exit, Time
          Exit, Breakeven, Other. The Manual Exit Cliff detector compares
          your manual exits against your planned exits, so this is more
          than a label.
        </LI>
        <LI>
          <UI>Post-Trade Emotion</UI> — Proud / Satisfied, Relieved,
          Regretful / Disappointed, Angry at myself, Euphoric
          (dangerous), Numb / Indifferent
        </LI>
      </UL>

      <H3>Free-text reflection</H3>
      <UL>
        <LI><UI>What were you thinking?</UI> — optional notes captured at the top of Reflection</LI>
        <LI><UI>What did I learn from this trade?</UI> — your key takeaway</LI>
        <LI><UI>What would I do differently next time?</UI> — your forward-looking note to yourself</LI>
      </UL>
      <P>
        These don't feed any detector — they're for you. But they're
        searchable and appear in Trade Review later, so a quick note
        here pays off when you're reviewing the trade a week later.
      </P>

      <H2>What unlocks what — the short version</H2>
      <P>
        If you want maximum detector coverage with minimum field-filling,
        these are the highest-leverage non-required fields:
      </P>
      <UL>
        <LI><UI>Stop Loss</UI> → 3 risk detectors</LI>
        <LI><UI>Setup Type</UI> → Setup Edge Ranking</LI>
        <LI><UI>Pre-Trade Emotional State</UI> + <UI>Fear Level</UI> + <UI>Greed Level</UI> → the Psychology suite</LI>
        <LI><UI>Market Condition</UI> + news event flag → Context detectors</LI>
        <LI><UI>Conviction Level</UI> → Conviction Calibration</LI>
      </UL>
      <P>
        The required fields (direction, prices, times, position size) already
        unlock the Behavior suite — Hour Cliff, Position Size Tilt,
        Win-Streak Euphoria, Time-in-Trade — automatically. The list above
        is what you add to get the other three suites firing too.
      </P>
      <P>
        Filling these consistently for ~30 trades is enough to get
        meaningful reads from most of the analytics tabs.
      </P>

      <H2>After you save</H2>
      <P>
        The trade lands in your Journal, your Dashboard's Recent Trades
        list, and updates every analytics suite that has the data it
        needs. To get coaching feedback on the trade you just logged,
        head to <UI>Trade Review</UI> — it'll appear at the top of the
        recent-trades list there.
      </P>
    </GuideLayout>
  );
}
